/**
 * useAnalytics.js - Hook React pour le tracking des analyses
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import TrackingService from '../services/TrackingService';

export const useAnalysisTracking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentAnalysisId = useRef(null);
  const startTime = useRef(null);
  const sessionId = useRef(TrackingService.generateSessionId());

  /**
   * Démarrer une nouvelle analyse
   */
  const startAnalysis = useCallback(() => {
    startTime.current = Date.now();
    setError(null);
    return startTime.current;
  }, []);

  /**
   * Tracker une analyse complète
   */
  const trackAnalysis = useCallback(async (analysisData) => {
    setLoading(true);
    try {
      const queryDuration = startTime.current ? Date.now() - startTime.current : 0;

      const analysisId = await TrackingService.trackAnalysis({
        sessionId: sessionId.current,
        queryDuration,
        ...analysisData
      });

      currentAnalysisId.current = analysisId;
      setError(null);
      return analysisId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Tracker la sauvegarde
   */
  const saveAnalysis = useCallback(async (analysisId, saveData) => {
    const id = analysisId || currentAnalysisId.current;
    if (!id) {
      console.warn('No analysis ID to save');
      return null;
    }

    try {
      return await TrackingService.trackSave(id, saveData);
    } catch (err) {
      console.error('Error saving analysis:', err);
      throw err;
    }
  }, []);

  /**
   * Tracker la consultation et terminer l'analyse
   */
  const completeAnalysis = useCallback(async (analysisId, action = 'COMPLETE') => {
    const id = analysisId || currentAnalysisId.current;
    if (!id) {
      console.warn('No analysis ID to complete');
      return null;
    }

    try {
      const timeSpent = startTime.current ? (Date.now() - startTime.current) / 1000 : 0;
      return await TrackingService.trackConsultation(id, timeSpent);
    } catch (err) {
      console.error('Error completing analysis:', err);
      throw err;
    }
  }, []);

  /**
   * Tracker la consultation sans terminer
   */
  const markAsConsidered = useCallback(async (analysisId, viewData) => {
    const id = analysisId || currentAnalysisId.current;
    if (!id) return null;

    try {
      return await TrackingService.trackConsultation(id, viewData?.timeSpentSeconds || 0);
    } catch (err) {
      console.error('Error marking as considered:', err);
      throw err;
    }
  }, []);

  /**
   * Réinitialiser l'état pour une nouvelle analyse
   */
  const resetAnalysis = useCallback(() => {
    currentAnalysisId.current = null;
    startTime.current = null;
    setError(null);
    setLoading(false);
  }, []);

  // Nettoyage
  useEffect(() => {
    return () => {
      if (currentAnalysisId.current && startTime.current) {
        const timeSpent = (Date.now() - startTime.current) / 1000;
        if (timeSpent > 1) {
          TrackingService.trackConsultation(currentAnalysisId.current, timeSpent);
        }
      }
    };
  }, []);

  return {
    trackAnalysis,
    saveAnalysis,
    markAsConsidered,
    completeAnalysis,
    startAnalysis,
    resetAnalysis,
    currentAnalysisId: currentAnalysisId.current,
    loading,
    error
  };
};

export default useAnalysisTracking;